import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Badge from "../common/Badge";
import Button from "../common/Button";
import EmptyState from "../common/EmptyState";
import Skeleton from "../common/Skeleton";
import {
  createAiConfig,
  deleteAiConfig,
  getAiConfig,
  getAiConfigs,
  getClients,
  updateAiConfig,
} from "../../services/aiConfigApi";
import { normalizeApiError } from "../../utils/errors";

// Voices supported by the calling-openai-asterisk module (OPENAI_VOICE).
const VOICE_OPTIONS = [
  "marin",
  "cedar",
  "alloy",
  "ash",
  "ballad",
  "coral",
  "echo",
  "sage",
  "shimmer",
  "verse",
];

const EMPTY_FORM = {
  id: "",
  client_id: "",
  name: "",
  status: "ACTIVE",
  is_default: false,
  company_name: "",
  company_timezone: "Asia/Calcutta",
  agent_name: "",
  agent_role: "AI receptionist",
  voice: "marin",
  openai_model: "",
  speed: "",
  greet_first: true,
  default_language: "English",
  supported_languages_text: "",
  // Single pasted system prompt (the whole agent instruction).
  system_prompt: "",
  // Advanced (optional) — blank means use the module's tested default.
  vad_threshold: "",
  vad_prefix_padding_ms: "",
  vad_silence_duration_ms: "",
  transcription_model: "",
};

function buildEmptyErrors() {
  return {
    name: "",
    company_name: "",
    company_timezone: "",
    system_prompt: "",
  };
}

function listToTextarea(values) {
  return Array.isArray(values) ? values.join("\n") : "";
}

function numberToInput(value) {
  return value === null || value === undefined ? "" : String(value);
}

function normalizeConfigToForm(config) {
  if (!config) {
    return EMPTY_FORM;
  }

  return {
    id: config.id || "",
    client_id: config.client_id ? String(config.client_id) : "",
    name: config.name || "",
    status: config.status || "ACTIVE",
    is_default: Boolean(config.is_default),
    company_name: config.company_name || "",
    company_timezone: config.company_timezone || "Asia/Calcutta",
    agent_name: config.agent_name || "",
    agent_role: config.agent_role || "AI receptionist",
    voice: config.voice || "marin",
    openai_model: config.openai_model || "",
    speed: numberToInput(config.speed),
    greet_first: config.greet_first == null ? true : Boolean(config.greet_first),
    default_language: config.default_language || "English",
    supported_languages_text: listToTextarea(config.supported_languages),
    system_prompt: config.system_prompt || "",
    vad_threshold: numberToInput(config.vad_threshold),
    vad_prefix_padding_ms: numberToInput(config.vad_prefix_padding_ms),
    vad_silence_duration_ms: numberToInput(config.vad_silence_duration_ms),
    transcription_model: config.transcription_model || "",
  };
}

// Optional knobs send `null` when blank so the backend stores NULL and the
// calling module falls back to its own tested default.
function optionalNumber(value) {
  const text = String(value ?? "").trim();
  if (!text) {
    return null;
  }
  const num = Number(text);
  return Number.isFinite(num) ? num : null;
}

function optionalText(value) {
  const text = String(value ?? "").trim();
  return text || null;
}

function buildPayload(form) {
  return {
    client_id: form.client_id ? Number(form.client_id) : null,
    name: form.name.trim(),
    status: form.status,
    is_default: Boolean(form.is_default),
    company_name: form.company_name.trim(),
    company_timezone: form.company_timezone.trim(),
    agent_name: form.agent_name.trim(),
    agent_role: form.agent_role.trim(),
    voice: form.voice.trim(),
    default_language: form.default_language.trim(),
    supported_languages: form.supported_languages_text,
    system_prompt: form.system_prompt,
    openai_model: optionalText(form.openai_model),
    speed: optionalNumber(form.speed),
    greet_first: Boolean(form.greet_first),
    vad_threshold: optionalNumber(form.vad_threshold),
    vad_prefix_padding_ms: optionalNumber(form.vad_prefix_padding_ms),
    vad_silence_duration_ms: optionalNumber(form.vad_silence_duration_ms),
    transcription_model: optionalText(form.transcription_model),
  };
}

function validateForm(form) {
  const errors = buildEmptyErrors();

  if (!String(form.name || "").trim()) {
    errors.name = "Configuration name is required.";
  }
  if (!String(form.company_name || "").trim()) {
    errors.company_name = "Company name is required.";
  }
  if (!String(form.company_timezone || "").trim()) {
    errors.company_timezone = "Company timezone is required.";
  }
  if (!String(form.system_prompt || "").trim()) {
    errors.system_prompt = "System prompt is required.";
  }

  return errors;
}

function getStatusVariant(config = {}) {
  if (config.is_default) {
    return "booked";
  }
  if (config.status === "ARCHIVED") {
    return "cancelled";
  }
  if (config.status === "INACTIVE") {
    return "info";
  }
  return "blue";
}

function getScopeLabel(config = {}, clientNameById = new Map()) {
  if (config.is_default) {
    return "Default fallback scope";
  }

  if (config.client_id) {
    return clientNameById.get(String(config.client_id)) || `Client ${config.client_id}`;
  }

  return "Global scope";
}

export default function AiRuntimeConfigManager({ showToast }) {
  const mountedRef = useRef(false);
  const [configsData, setConfigsData] = useState({ items: [], meta: {} });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState(buildEmptyErrors());
  const [previewConfig, setPreviewConfig] = useState(null);
  const [actionBusyId, setActionBusyId] = useState("");

  const clientNameById = useMemo(
    () =>
      new Map(
        clients.map((client) => [
          String(client.client_id),
          client.name || `Client ${client.client_id}`,
        ])
      ),
    [clients]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [configsResult, clientsResult] = await Promise.all([
        getAiConfigs({ page: 1, limit: 100 }),
        getClients(),
      ]);

      if (!mountedRef.current) {
        return;
      }

      setConfigsData(configsResult);
      setClients(clientsResult);
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort && mountedRef.current) {
        setError(normalizedError.message || "Unable to load AI calling agents.");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadData().catch(() => undefined);

    return () => {
      mountedRef.current = false;
    };
  }, [loadData]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setFormErrors(buildEmptyErrors());
    setPreviewConfig(null);
  };

  const handleEdit = async (configId) => {
    setActionBusyId(String(configId));

    try {
      const config = await getAiConfig(configId);
      if (!mountedRef.current || !config) {
        return;
      }

      setForm(normalizeConfigToForm(config));
      setFormErrors(buildEmptyErrors());
      setPreviewConfig(config);
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setActionBusyId("");
      }
    }
  };

  const handlePreview = async (configId) => {
    setActionBusyId(String(configId));

    try {
      const config = await getAiConfig(configId);
      if (mountedRef.current) {
        setPreviewConfig(config || null);
      }
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setActionBusyId("");
      }
    }
  };

  const handleArchive = async (configId) => {
    if (!window.confirm("Archive this AI calling agent?")) {
      return;
    }

    setActionBusyId(String(configId));
    try {
      await deleteAiConfig(configId);
      showToast("AI calling agent archived successfully.", false, 4000);
      await loadData();
      if (mountedRef.current && previewConfig?.id === configId) {
        setPreviewConfig(null);
      }
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setActionBusyId("");
      }
    }
  };

  const handleStatusToggle = async (config) => {
    const nextStatus = config.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setActionBusyId(String(config.id));

    try {
      await updateAiConfig(config.id, { status: nextStatus });
      showToast(
        nextStatus === "ACTIVE"
          ? "AI calling agent activated. Any other active agent for the same client was set to inactive."
          : "AI calling agent set to inactive.",
        false,
        5000
      );
      await loadData();
      if (mountedRef.current && previewConfig?.id === config.id) {
        setPreviewConfig(await getAiConfig(config.id));
      }
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setActionBusyId("");
      }
    }
  };

  const handleSubmit = async () => {
    const nextErrors = validateForm(form);
    setFormErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(form);
      const saved = form.id
        ? await updateAiConfig(form.id, payload)
        : await createAiConfig(payload);

      showToast(
        payload.status === "ACTIVE"
          ? "AI calling agent saved and activated. Any other active agent for the same client was set to inactive."
          : form.id
            ? "AI calling agent updated successfully."
            : "AI calling agent created successfully.",
        false,
        4000
      );
      if (mountedRef.current) {
        setPreviewConfig(saved);
      }
      resetForm();
      await loadData();
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  };

  return (
    <>
      <section id="ai-runtime-config" className="chart-card settings-panel settings-panel-span-full">
        <div className="settings-panel-header">
          <div className="team-card-heading">AI Calling Agent</div>
          <div className="page-subtitle">
            Set up your AI calling agent - company details, voice, languages, and a single
            system prompt.
          </div>
        </div>

        <div className="settings-inline-help" style={{ marginBottom: "12px" }}>
          Only one agent can be <strong>Active</strong> for the same client. Saving or activating another one sets the existing active agent to <strong>Inactive</strong>.
        </div>

        <div className="settings-fields">
          <div className="settings-script-grid">
            <label className="settings-field">
              <span className="settings-label">Configuration Name</span>
              <input
                className="settings-input"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, name: event.target.value }))
                }
                placeholder="Primary client voice configuration"
              />
              {formErrors.name ? (
                <span className="settings-inline-error">{formErrors.name}</span>
              ) : null}
            </label>

            <label className="settings-field">
              <span className="settings-label">Client</span>
              <select
                className="settings-select"
                value={form.client_id}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    client_id: event.target.value,
                    is_default: event.target.value ? current.is_default : true,
                  }))
                }
              >
                <option value="">Default / global fallback</option>
                {clients.map((client) => (
                  <option key={client.client_id} value={client.client_id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="settings-field">
              <span className="settings-label">Status</span>
              <select
                className="settings-select"
                value={form.status}
                onChange={(event) =>
                  setForm((current) => ({ ...current, status: event.target.value }))
                }
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </label>

            <label className="settings-field">
              <span className="settings-label">Company Name</span>
              <input
                className="settings-input"
                value={form.company_name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, company_name: event.target.value }))
                }
                placeholder="Example Client Company"
              />
              {formErrors.company_name ? (
                <span className="settings-inline-error">{formErrors.company_name}</span>
              ) : null}
            </label>

            <label className="settings-field">
              <span className="settings-label">Company Timezone</span>
              <input
                className="settings-input"
                value={form.company_timezone}
                onChange={(event) =>
                  setForm((current) => ({ ...current, company_timezone: event.target.value }))
                }
                placeholder="Asia/Calcutta"
              />
              {formErrors.company_timezone ? (
                <span className="settings-inline-error">{formErrors.company_timezone}</span>
              ) : null}
            </label>

            <label className="settings-field">
              <span className="settings-label">Agent Name</span>
              <input
                className="settings-input"
                value={form.agent_name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, agent_name: event.target.value }))
                }
                placeholder="Example Client AI Receptionist"
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Agent Role / Persona</span>
              <input
                className="settings-input"
                value={form.agent_role}
                onChange={(event) =>
                  setForm((current) => ({ ...current, agent_role: event.target.value }))
                }
                placeholder="AI receptionist"
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Voice</span>
              <select
                className="settings-select"
                value={form.voice}
                onChange={(event) =>
                  setForm((current) => ({ ...current, voice: event.target.value }))
                }
              >
                {VOICE_OPTIONS.map((voice) => (
                  <option key={voice} value={voice}>
                    {voice}
                  </option>
                ))}
              </select>
            </label>

            <label className="settings-field">
              <span className="settings-label">Model</span>
              <input
                className="settings-input"
                value={form.openai_model}
                onChange={(event) =>
                  setForm((current) => ({ ...current, openai_model: event.target.value }))
                }
                placeholder="gpt-realtime-mini"
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Speed</span>
              <input
                className="settings-input"
                type="number"
                min="0.25"
                max="1.5"
                step="0.05"
                value={form.speed}
                onChange={(event) =>
                  setForm((current) => ({ ...current, speed: event.target.value }))
                }
                placeholder="1.0"
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Default Language</span>
              <input
                className="settings-input"
                value={form.default_language}
                onChange={(event) =>
                  setForm((current) => ({ ...current, default_language: event.target.value }))
                }
                placeholder="English"
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Supported Languages</span>
              <textarea
                className="settings-input settings-textarea"
                rows="4"
                value={form.supported_languages_text}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    supported_languages_text: event.target.value,
                  }))
                }
                placeholder={"English\nHindi\nTamil"}
              />
            </label>
          </div>

          <label className="settings-toggle-row">
            <span>Use as default AI calling agent</span>
            <input
              type="checkbox"
              checked={Boolean(form.is_default)}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  is_default: event.target.checked,
                  client_id: event.target.checked ? "" : current.client_id,
                }))
              }
            />
          </label>

          <label className="settings-toggle-row">
            <span>Agent greets first (speaks the opening line on connect)</span>
            <input
              type="checkbox"
              checked={Boolean(form.greet_first)}
              onChange={(event) =>
                setForm((current) => ({ ...current, greet_first: event.target.checked }))
              }
            />
          </label>

          <label className="settings-field">
            <span className="settings-label">System Prompt</span>
            <div className="settings-inline-help" style={{ marginBottom: "6px" }}>
              Paste the complete system prompt for this agent. This is used exactly
              as written for every call (persona, greeting wording, conversation
              flow, objection handling, and closing all live here).
            </div>
            <textarea
              className="settings-input settings-textarea"
              rows="24"
              value={form.system_prompt}
              onChange={(event) =>
                setForm((current) => ({ ...current, system_prompt: event.target.value }))
              }
              placeholder={'You are "Sara", a warm, professional voice assistant for ...'}
            />
            {formErrors.system_prompt ? (
              <span className="settings-inline-error">{formErrors.system_prompt}</span>
            ) : null}
          </label>

          <details className="settings-field">
            <summary className="settings-label" style={{ cursor: "pointer" }}>
              Advanced (optional)
            </summary>
            <div className="settings-inline-help" style={{ margin: "8px 0" }}>
              Leave blank to use the tested defaults. Only change these if you know
              what they do.
            </div>
            <div className="settings-script-grid">
              <label className="settings-field">
                <span className="settings-label">Turn-detection threshold</span>
                <input
                  className="settings-input"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={form.vad_threshold}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, vad_threshold: event.target.value }))
                  }
                  placeholder="0.5"
                />
              </label>

              <label className="settings-field">
                <span className="settings-label">Prefix padding (ms)</span>
                <input
                  className="settings-input"
                  type="number"
                  min="0"
                  max="5000"
                  step="10"
                  value={form.vad_prefix_padding_ms}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      vad_prefix_padding_ms: event.target.value,
                    }))
                  }
                  placeholder="300"
                />
              </label>

              <label className="settings-field">
                <span className="settings-label">Silence duration (ms)</span>
                <input
                  className="settings-input"
                  type="number"
                  min="0"
                  max="5000"
                  step="10"
                  value={form.vad_silence_duration_ms}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      vad_silence_duration_ms: event.target.value,
                    }))
                  }
                  placeholder="500"
                />
              </label>

              <label className="settings-field">
                <span className="settings-label">Transcription model</span>
                <input
                  className="settings-input"
                  value={form.transcription_model}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      transcription_model: event.target.value,
                    }))
                  }
                  placeholder="gpt-4o-mini-transcribe"
                />
              </label>
            </div>
          </details>

          <div className="page-actions">
            <Button
              variant="gold"
              disabled={saving}
              onClick={() =>
                setPreviewConfig({
                  ...buildPayload(form),
                  id: form.id || "draft",
                  system_prompt: form.system_prompt,
                })
              }
            >
              Preview
            </Button>
            <Button variant="primary" disabled={saving} onClick={handleSubmit}>
              {saving ? "Saving..." : form.id ? "Save Agent" : "Create Agent"}
            </Button>
            <Button disabled={saving} onClick={resetForm}>
              Clear
            </Button>
            <Button disabled={loading} onClick={() => loadData().catch(() => undefined)}>
              Refresh
            </Button>
          </div>
        </div>
      </section>

      <section className="chart-card settings-panel settings-panel-span-full">
        <div className="settings-panel-header">
          <div className="team-card-heading">Your AI Calling Agents</div>
          <div className="page-subtitle">
            Your saved AI calling agents and which one is currently active.
          </div>
        </div>

        {loading ? (
          <div className="empty-state">
            <Skeleton style={{ height: 16, width: 220, margin: "0 auto 8px" }} />
            <Skeleton style={{ height: 12, width: 140, margin: "0 auto" }} />
          </div>
        ) : error ? (
          <EmptyState title="Unable to load AI calling agents" subtitle={error} />
        ) : !configsData.items.length ? (
          <EmptyState
            title="No AI calling agents"
            subtitle="Create an AI calling agent to manage company details, voice, languages, and scripts."
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Voice</th>
                  <th>Languages</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configsData.items.map((config) => {
                  const isBusy = actionBusyId === String(config.id);
                  return (
                    <tr key={config.id}>
                      <td>
                        <div>
                          <strong>{config.name}</strong>
                          <div className="muted-text">
                            {config.company_name || "Unnamed company"}
                          </div>
                        </div>
                      </td>
                      <td>
                        {config.is_default
                          ? "Default fallback"
                          : clientNameById.get(String(config.client_id)) || "Client not set"}
                        <div className="muted-text">{getScopeLabel(config, clientNameById)}</div>
                      </td>
                      <td>
                        <Badge variant={getStatusVariant(config)}>
                          {config.status === "ACTIVE" ? "Active" : config.status === "INACTIVE" ? "Inactive" : "Archived"}
                        </Badge>
                      </td>
                      <td>{config.voice || "marin"}</td>
                      <td>
                        {Array.isArray(config.supported_languages) &&
                        config.supported_languages.length
                          ? config.supported_languages.join(", ")
                          : config.default_language || "English"}
                      </td>
                      <td className="td-actions">
                        <div className="settings-script-actions">
                          <Button disabled={isBusy} onClick={() => handlePreview(config.id)}>
                            Preview
                          </Button>
                          <Button disabled={isBusy} onClick={() => handleEdit(config.id)}>
                            Edit
                          </Button>
                          <Button
                            variant={config.status === "ACTIVE" ? "gold" : "primary"}
                            disabled={isBusy || config.status === "ARCHIVED"}
                            onClick={() => handleStatusToggle(config)}
                          >
                            {config.status === "ACTIVE" ? "Set Inactive" : "Activate"}
                          </Button>
                          <Button
                            variant="danger"
                            disabled={isBusy || config.status === "ARCHIVED"}
                            onClick={() => handleArchive(config.id)}
                          >
                            Archive
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {previewConfig ? (
        <section className="chart-card settings-panel settings-panel-span-full">
          <div className="settings-panel-header">
            <div className="team-card-heading">Agent Preview</div>
            <div className="page-subtitle">
              Review the selected AI calling agent before saving or activating it.
            </div>
          </div>

          <div className="settings-script-preview">
            <div className="settings-script-summary">
              <div>
                <div className="settings-label">Selected Agent</div>
                <div className="settings-script-summary-text">
                  <strong>{previewConfig.name || "Draft agent"}</strong>
                  {previewConfig.company_name ? ` for ${previewConfig.company_name}` : ""}
                </div>
              </div>
              <Badge variant={getStatusVariant(previewConfig)}>
                {previewConfig.status === "ACTIVE" ? "Active" : previewConfig.status === "INACTIVE" ? "Inactive" : previewConfig.status || "Draft"}
              </Badge>
            </div>

            <div className="settings-script-grid">
              <div className="settings-field">
                <span className="settings-label">Agent</span>
                <div className="settings-inline-help">
                  {previewConfig.agent_name || "Not set"} - {previewConfig.agent_role || "AI calling agent"}
                </div>
              </div>
              <div className="settings-field">
                <span className="settings-label">Voice</span>
                <div className="settings-inline-help">{previewConfig.voice || "Not set"}</div>
              </div>
              <div className="settings-field">
                <span className="settings-label">Languages</span>
                <div className="settings-inline-help">
                  {Array.isArray(previewConfig.supported_languages) && previewConfig.supported_languages.length
                    ? previewConfig.supported_languages.join(", ")
                    : previewConfig.default_language || "English"}
                </div>
              </div>
            </div>

            <pre className="settings-script-preview-content">
              {previewConfig.system_prompt ||
                previewConfig.published_system_prompt_text ||
                "No preview text available yet."}
            </pre>
          </div>
        </section>
      ) : null}
    </>
  );
}
